<template>
    <div class="widget-counter-agents-status-form">
        <div
            class="flex items-center mb-5 gap-2"
            :style="statusIconColor"
        >
            <WidgetStatusIcon
                :icon="selectedOption?.icon"
                :color="'var(--status-svg-color)'"
                :size="24"
                class="widget-status-icon rounded"
            />
            <div class="text-main-lg font-medium text-gray-700">
                {{ t(selectedOption?.text) }}
            </div>
        </div>
        <div>
            <VcFormItem
                :label="t('general.status')"
            >
                <VcSelect
                    v-model="activeStatus"
                    :placeholder="t('common.selectStatus')"
                    :options="statusList"
                    :config="{
                        labelKey: 'text',
                        valueKey: 'value'
                    }"
                    class="w-full"
                    @update:model-value="onStatusChange"
                >
                    <template #option="{ option }">
                        <div
                            class="flex items-center gap-1.5"
                            :style="styleForStatusIconColor(option)"
                        >
                            <WidgetStatusIcon
                                :icon="option?.icon"
                                :color="'var(--status-svg-color)'"
                                :size="16"
                                class="widget-status-icon rounded"
                            />
                            <span class="w-16 mx-1">{{ t(option.text) }}</span>
                        </div>
                    </template>
                </VcSelect>
            </VcFormItem>
        </div>
        <div
            v-if="useContentTitle"
        >
            <VcFormItem
                :label="t('widget.contentTitle')"
            >
                <VcInput v-model="contentTitleModel" />
            </VcFormItem>
        </div>
        <div class="mb-3 space-y-1.5">
            <VcCheckbox
                v-model="widgetLayoutShowStatusText"
            >
                {{ t('status.show.text') }}
            </VcCheckbox>
            <VcCheckbox
                v-model="widgetLayoutDisplayBorder"
            >
                {{ t('status.display.border') }}
            </VcCheckbox>
        </div>
        <div class="mb-5">
            <WidgetInnerAlignment
                :content="widgetContentAlignment"
                :layout="widgetContentLayout"
                @update:content="$evt => widgetContentAlignment = $evt"
                @update:layout="$evt => widgetContentLayout = $evt"
            />
        </div>
        <div class="mb-5">
            <WidgetCardLayoutConfigs v-model="widgetLayoutModel" />
        </div>
        <div class="mb-5">
            <VcCollapseItem
                :title="t('settings.sounds')"
                name="sounds"
                class="simple-collapse"
                :has-border="false"
            >
                <WidgetStatusCardSounds v-model="widgetLayoutModel" />
            </VcCollapseItem>
        </div>
        <div
            v-if="widgetConfigData.filters"
            class="mb-5"
        >
            <VcCollapseItem
                :title="t('settings.filters')"
                name="filters"
                class="simple-collapse"
                :has-border="false"
            >
                <div>
                    <div
                        v-for="(filter, index) in widgetConfigData.filters"
                        :key="`filter-${index}`"
                    >
                        <RedesignWidgetConfigEntityTree
                            v-if="isAutoComplete(filter)"
                            :key="`filter-tree-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            class="mb-4"
                            @update:value="updateFilterItem"
                        />
                        <RedesignWidgetConfigSelect
                            v-if="isMultiSelect(filter)"
                            :key="`filter-select-${filter.ParameterID}`"
                            :value="getWidgetConfig(filter)"
                            @update:value="updateFilterItem"
                        />
                    </div>
                </div>
            </VcCollapseItem>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVModel } from '@vueuse/core'
import get from 'lodash/get'
import { VcFormItem, VcSelect, VcInput, VcCheckbox, VcCollapseItem } from '@voicenter-team/voicenter-ui-plus'
import { WidgetConfig, WidgetLayout } from '@/types/dashboard'
import {
    getWidgetConfigByTemplateId,
    getWidgetConfigMapped,
    getWidgetStatusList,
    getWidgetStatusType,
    isAutoComplete,
    isMultiSelect,
    styleForStatusIconColor
} from '@/components/RedesignComponents/Widgets/widget.helper'
import { CounterWidgetsOnlyStatuses, statusCardsWithContentTitle } from '@/enum/redesignStatusTypes'
import { getSelectedAccountStatusTextById } from '@/composables/globalData/useEntitiesData'
import RedesignWidgetConfigSelect from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigSelect.vue'
import WidgetStatusCardSounds from '@/components/RedesignComponents/Widgets/Common/WidgetStatusCardSounds.vue'
import WidgetCardLayoutConfigs
    from '@/components/RedesignComponents/Widgets/Edit/WidgetCardsForms/WidgetCardLayoutConfigs.vue'
import WidgetInnerAlignment from '@/components/RedesignComponents/Widgets/Common/WidgetInnerAlignment.vue'
import WidgetStatusIcon from '@/components/RedesignComponents/Cards/WidgetStatusIcon.vue'
import RedesignWidgetConfigEntityTree
    from '@/components/RedesignComponents/Widgets/Common/RedesignWidgetConfigEntityTree.vue'

const { t } = useI18n()

interface IProps {
    modelValue: WidgetLayout
    widgetConfig: WidgetConfig[]
    templateId: number
}

const props = defineProps<IProps>()
const emit = defineEmits<{
    (e: 'update:modelValue', val: WidgetLayout): void
    (e: 'update:config', val: WidgetConfig): void
}>()

const widgetLayoutModel = useVModel(props, 'modelValue', emit, {
    passive: true
})

/* Computed */
const activeStatus = computed({
    get () {
        return widgetLayoutModel.value.status
    },
    set (val) {
        widgetLayoutModel.value.status = val
    }
})
const contentTitleModel = computed({
    get () {
        return activeStatus.value !== undefined && widgetLayoutModel.value?.ContentTitle ? widgetLayoutModel.value?.ContentTitle[activeStatus.value] : ''
    },
    set (val) {
        if (activeStatus.value === undefined) {
            return
        }
        if (!widgetLayoutModel.value.ContentTitle) {
            widgetLayoutModel.value.ContentTitle = {}
        }
        widgetLayoutModel.value.ContentTitle = {
            ...widgetLayoutModel.value.ContentTitle,
            [activeStatus.value]: val
        }
    }
})
const widgetLayoutDisplayBorder = computed({
    get () {
        return widgetLayoutModel.value.displayBorder === undefined ? true : widgetLayoutModel.value.displayBorder
    },
    set (val) {
        widgetLayoutModel.value.displayBorder = val
    }
})
const widgetLayoutShowStatusText = computed({
    get () {
        return widgetLayoutModel.value.showText === undefined ? true : widgetLayoutModel.value.showText
    },
    set (val) {
        widgetLayoutModel.value.showText = val
    }
})
const widgetContentAlignment = computed({
    get () {
        return widgetLayoutModel.value?.contentAlignment === undefined ? 'center' : widgetLayoutModel.value.contentAlignment
    },
    set (val) {
        if (widgetLayoutModel.value.contentAlignment === undefined) {
            widgetLayoutModel.value = {
                ...widgetLayoutModel.value,
                contentAlignment: 'center'
            }
        }
        widgetLayoutModel.value.contentAlignment = val
    }
})
const widgetContentLayout = computed({
    get () {
        return widgetLayoutModel.value?.contentLayout === undefined ? 'default' : widgetLayoutModel.value.contentLayout
    },
    set (val) {
        if (widgetLayoutModel.value.contentLayout === undefined) {
            widgetLayoutModel.value = {
                ...widgetLayoutModel.value,
                contentLayout: 'default'
            }
        }
        widgetLayoutModel.value.contentLayout = val
    }
})
const useContentTitle = computed(() => {
    return statusCardsWithContentTitle.includes(props.templateId)
})
const statusIconColor = computed(() => {
    return styleForStatusIconColor({ value: activeStatus.value })
})

const selectedOption = computed(() => {
    return getWidgetStatusType(activeStatus.value)
})
const statusList = computed(() => {
    return getWidgetStatusList()
})
const widgetConfigData = computed(() => {
    return getWidgetConfigMapped(props.widgetConfig)
})

/* Methods */
function getStatusLabel (status: number): string {
    const isCounterWidgetOnly = (Object.values(CounterWidgetsOnlyStatuses) as number[]).includes(status)
    if (isCounterWidgetOnly) {
        const statusData = getWidgetStatusType(status)
        return statusData?.text ?? ''
    }
    return getSelectedAccountStatusTextById(status)
}

function onStatusChange (status: number) {
    if (useContentTitle.value) {
        const labelKey = getStatusLabel(status)
        contentTitleModel.value = String(get(widgetLayoutModel.value, `ContentTitle[${status}]`, t(labelKey)))
    }
}

function getWidgetConfig (config: WidgetConfig) {
    return getWidgetConfigByTemplateId(config, props.templateId)
}

function updateFilterItem (filterData: WidgetConfig) {
    emit('update:config', filterData)
}

</script>
